class MRCardAlbum {
  constructor(root) {
    this.root = root;
    this.dataNode = root.querySelector('[data-mr-album-data]');
    this.syncConfigNode = root.querySelector('[data-mr-album-sync-config]');
    this.syncLoginLink = root.querySelector('[data-sync-login]');
    this.grid = root.querySelector('[data-card-grid]');
    this.browser = root.querySelector('[data-album-browser]');
    this.emptyState = root.querySelector('[data-empty-state]');
    this.emptyTitle = root.querySelector('[data-empty-title]');
    this.emptyCopy = root.querySelector('[data-empty-copy]');
    this.searchInput = root.querySelector('[data-card-search]');
    this.modal = root.querySelector('[data-card-modal]');
    this.modalImage = root.querySelector('[data-modal-image]');
    this.modalCollection = root.querySelector('[data-modal-collection]');
    this.modalNumber = root.querySelector('[data-modal-number]');
    this.modalTitle = root.querySelector('[data-modal-title]');
    this.modalDescription = root.querySelector('[data-modal-description]');
    this.modalOwnedButton = root.querySelector('[data-modal-owned-toggle]');
    this.copyLinkButton = root.querySelector('[data-copy-card-link]');
    this.completionDialog = root.querySelector('[data-completion-dialog]');
    this.completionTitle = root.querySelector('[data-completion-title]');
    this.completionCount = root.querySelector('[data-completion-count]');
    this.completionTotal = root.querySelector('[data-completion-total]');
    this.completionConfetti = root.querySelector('[data-completion-confetti]');
    this.completionShowTimer = null;
    this.completionRippleTimer = null;
    this.storageBaseKey = 'mr-card-album-owned-v1';
    this.storageAvailable = true;
    this.usedLegacyStorage = false;
    this.accountReady = false;
    this.syncState = 'local';
    this.syncQueue = Promise.resolve();
    this.ownedCards = new Set();
    this.currentCollection = null;
    this.currentCardIndex = 0;
    this.ownershipFilter = 'all';
    this.query = '';

    if (!this.dataNode || !this.grid) return;

    try {
      this.data = JSON.parse(this.dataNode.textContent);
    } catch (error) {
      console.error('Impossibile caricare i dati dell’album MaglieRandom.', error);
      return;
    }

    if (!Array.isArray(this.data.collections) || !this.data.collections.length) return;

    this.syncConfig = this.readSyncConfig();
    this.accountMode = Boolean(
      this.syncConfig.enabled &&
      this.syncConfig.loggedIn &&
      this.syncConfig.customerId
    );
    this.storageKey = this.accountMode
      ? `${this.storageBaseKey}-customer-${this.syncConfig.customerId}`
      : this.storageBaseKey;
    this.migrationKey = this.accountMode
      ? `${this.storageBaseKey}-migrated-${this.syncConfig.customerId}`
      : null;

    this.collectionMap = new Map(
      this.data.collections.map((collection) => [collection.id, collection])
    );
    this.allCards = this.data.collections.flatMap((collection) => collection.cards);
    this.validCardIds = new Set(this.allCards.map((card) => card.id));
    this.loadOwnedCards();

    this.bindEvents();
    this.loadInitialState();
    this.updateProgress();
    this.syncQueue = this.initializeAccountSync();
  }

  readSyncConfig() {
    const fallback = {
      enabled: false,
      loggedIn: false,
      customerId: null,
      endpoint: '/apps/mr-card-album/collection',
      loginUrl: '/account/login',
    };

    if (!this.syncConfigNode) return fallback;

    try {
      const parsed = JSON.parse(this.syncConfigNode.textContent);
      return {
        enabled: parsed.enabled === true,
        loggedIn: parsed.loggedIn === true,
        customerId: parsed.customerId ? String(parsed.customerId) : null,
        endpoint:
          typeof parsed.endpoint === 'string' && parsed.endpoint.startsWith('/')
            ? parsed.endpoint
            : fallback.endpoint,
        loginUrl:
          typeof parsed.loginUrl === 'string' && parsed.loginUrl.startsWith('/')
            ? parsed.loginUrl
            : fallback.loginUrl,
      };
    } catch (error) {
      return fallback;
    }
  }

  bindEvents() {
    this.root.querySelectorAll('[data-collection-option]').forEach((button) => {
      button.addEventListener('click', () => {
        this.selectCollection(button.dataset.collectionOption, { scroll: true });
      });
    });

    this.root.querySelectorAll('[data-collection-filter]').forEach((button) => {
      button.addEventListener('click', () => {
        this.selectCollection(button.dataset.collectionFilter);
      });
    });

    this.root.querySelectorAll('[data-ownership-filter]').forEach((button) => {
      button.addEventListener('click', () => {
        this.ownershipFilter = button.dataset.ownershipFilter;
        this.updateOwnershipFilterButtons();
        this.renderCards();
      });
    });

    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => {
        this.query = this.normalize(this.searchInput.value);
        this.renderCards();
      });
    }

    this.grid.addEventListener('click', (event) => {
      const cardElement = event.target.closest('[data-card-index]');
      if (!cardElement) return;

      const index = Number(cardElement.dataset.cardIndex);
      const card = this.currentCollection?.cards[index];
      if (!card) return;

      if (event.target.closest('[data-owned-toggle]')) {
        this.toggleOwned(card.id);
        return;
      }

      if (event.target.closest('[data-card-open]')) {
        this.openCard(index);
      }
    });

    this.root.querySelector('[data-modal-close]')?.addEventListener('click', () => {
      this.closeModal();
    });

    this.root.querySelector('[data-modal-previous]')?.addEventListener('click', () => {
      this.moveCard(-1);
    });

    this.root.querySelector('[data-modal-next]')?.addEventListener('click', () => {
      this.moveCard(1);
    });

    this.modalOwnedButton?.addEventListener('click', () => {
      const card = this.currentCollection?.cards[this.currentCardIndex];
      if (card) this.toggleOwned(card.id);
    });

    this.copyLinkButton?.addEventListener('click', () => this.copyCardLink());

    this.modal?.addEventListener('click', (event) => {
      if (event.target === this.modal) this.closeModal();
    });

    this.modal?.addEventListener('close', () => {
      this.updateUrl({ card: null });
    });

    this.modal?.addEventListener('keydown', (event) => {
      if (!this.modal?.open) return;
      if (event.key === 'ArrowLeft') this.moveCard(-1);
      if (event.key === 'ArrowRight') this.moveCard(1);
    });

    this.root.querySelector('[data-completion-close]')?.addEventListener('click', () => {
      this.closeCompletion();
    });

    this.completionDialog?.addEventListener('click', (event) => {
      if (event.target === this.completionDialog) this.closeCompletion();
    });

    this.completionDialog?.addEventListener('close', () => {
      this.completionDialog.classList.remove('is-visible');
    });
  }

  loadInitialState() {
    const url = new URL(window.location.href);
    const requestedCollection = url.searchParams.get('collection');
    const requestedCard = url.searchParams.get('card');
    const fallbackCollection = this.data.collections[0].id;
    const initialCollection = this.collectionMap.has(requestedCollection)
      ? requestedCollection
      : fallbackCollection;

    this.selectCollection(initialCollection, { updateUrl: Boolean(requestedCollection) });

    if (!requestedCard) return;

    const index = this.currentCollection.cards.findIndex((card) => card.id === requestedCard);
    if (index >= 0) {
      window.requestAnimationFrame(() => this.openCard(index, { updateUrl: false }));
    }
  }

  loadOwnedCards() {
    try {
      let storedValue = window.localStorage.getItem(this.storageKey);

      if (this.accountMode && storedValue === null) {
        storedValue = window.localStorage.getItem(this.storageBaseKey);
        this.usedLegacyStorage = storedValue !== null;
      }

      if (!storedValue) return;

      const storedCards = JSON.parse(storedValue);
      if (!Array.isArray(storedCards)) return;

      this.ownedCards = new Set(
        storedCards.filter((cardId) => this.validCardIds.has(cardId))
      );
    } catch (error) {
      this.storageAvailable = false;
      this.ownedCards = new Set();
    }
  }

  saveOwnedCards() {
    try {
      window.localStorage.setItem(
        this.storageKey,
        JSON.stringify(Array.from(this.ownedCards).sort())
      );
      this.storageAvailable = true;
    } catch (error) {
      this.storageAvailable = false;
    }

    this.updateStorageNote();
  }

  async initializeAccountSync() {
    if (!this.syncConfig.enabled) {
      this.setSyncState('local');
      return;
    }

    if (!this.accountMode) {
      this.setSyncState('signed_out');
      return;
    }

    this.setSyncState('loading');

    try {
      let remoteState = await this.requestAccountState();
      let alreadyMigrated = false;

      if (this.migrationKey && this.storageAvailable) {
        alreadyMigrated = window.localStorage.getItem(this.migrationKey) === 'true';
      }

      if (!alreadyMigrated && this.ownedCards.size) {
        remoteState = await this.requestAccountState({
          operation: 'merge',
          ownedCards: Array.from(this.ownedCards),
        });
      }

      this.accountReady = true;
      this.setSyncState('synced');
      this.applyServerCards(remoteState.ownedCards);

      if (this.migrationKey && this.storageAvailable) {
        window.localStorage.setItem(this.migrationKey, 'true');
        if (this.usedLegacyStorage) window.localStorage.removeItem(this.storageBaseKey);
      }
    } catch (error) {
      this.accountReady = false;
      this.setSyncState(error.loginRequired ? 'signed_out' : 'error');
      console.warn('Sincronizzazione album non disponibile; uso il salvataggio locale.', error);
    }
  }

  async requestAccountState(operation) {
    const options = {
      method: operation ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    };

    if (operation) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(operation);
    }

    const response = await window.fetch(this.syncConfig.endpoint, options);
    const contentType = response.headers.get('content-type') || '';
    const payload = contentType.includes('application/json')
      ? await response.json()
      : null;

    if (!response.ok || !payload || !Array.isArray(payload.ownedCards)) {
      const error = new Error(payload?.error || `Sincronizzazione fallita (${response.status})`);
      error.loginRequired = response.status === 401 && payload?.loginRequired === true;
      throw error;
    }

    return payload;
  }

  applyServerCards(cards) {
    this.ownedCards = new Set(
      cards.filter((cardId) => this.validCardIds.has(cardId))
    );
    this.saveOwnedCards();
    this.updateProgress();
    this.renderCards();

    const currentCard = this.currentCollection?.cards[this.currentCardIndex];
    if (currentCard) this.updateOwnedButton(this.modalOwnedButton, currentCard);
  }

  queueAccountUpdate(cardId, owned) {
    if (!this.accountMode) return;

    this.syncQueue = this.syncQueue
      .catch(() => undefined)
      .then(() => this.persistCardState(cardId, owned));
  }

  async persistCardState(cardId, owned) {
    this.setSyncState('saving');

    try {
      const remoteState = await this.requestAccountState({
        operation: 'set',
        cardId,
        owned,
      });
      this.accountReady = true;
      this.setSyncState('synced');
      this.applyServerCards(remoteState.ownedCards);
    } catch (error) {
      this.accountReady = false;
      this.setSyncState(error.loginRequired ? 'signed_out' : 'error');
      console.warn('Carta salvata sul dispositivo, ma non ancora nell’account.', error);
    }
  }

  selectCollection(collectionId, options = {}) {
    const collection = this.collectionMap.get(collectionId);
    if (!collection) return;

    this.currentCollection = collection;
    this.query = '';
    if (this.searchInput) this.searchInput.value = '';

    this.root.querySelectorAll('[data-collection-option]').forEach((button) => {
      button.setAttribute(
        'aria-pressed',
        String(button.dataset.collectionOption === collectionId)
      );
    });

    this.root.querySelectorAll('[data-collection-filter]').forEach((button) => {
      button.setAttribute(
        'aria-pressed',
        String(button.dataset.collectionFilter === collectionId)
      );
    });

    this.root.querySelector('[data-collection-kicker]').textContent =
      `Collezione ${collection.number}`;
    this.root.querySelector('[data-collection-title]').textContent = collection.title;
    this.root.querySelector('[data-collection-description]').textContent = collection.description;
    this.root.querySelector('[data-collection-count]').textContent = collection.cards.length;

    this.updateProgress();
    this.renderCards();

    if (options.updateUrl !== false) {
      this.updateUrl({ collection: collectionId, card: null });
    }

    if (options.scroll) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.browser.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }
  }

  renderCards() {
    if (!this.currentCollection) return;

    const cards = this.currentCollection.cards.filter((card) => {
      const matchesQuery = this.normalize(card.name).includes(this.query);
      const isOwned = this.ownedCards.has(card.id);
      const matchesOwnership =
        this.ownershipFilter === 'all' ||
        (this.ownershipFilter === 'owned' && isOwned) ||
        (this.ownershipFilter === 'missing' && !isOwned);

      return matchesQuery && matchesOwnership;
    });
    const fragment = document.createDocumentFragment();

    cards.forEach((card) => {
      const actualIndex = this.currentCollection.cards.indexOf(card);
      const cardElement = document.createElement('article');
      const openButton = document.createElement('button');
      const imageWrap = document.createElement('span');
      const image = document.createElement('img');
      const copy = document.createElement('span');
      const number = document.createElement('span');
      const name = document.createElement('span');
      const ownedButton = document.createElement('button');

      cardElement.className = 'mr-album__card';
      cardElement.dataset.cardIndex = String(actualIndex);
      cardElement.dataset.cardId = card.id;
      cardElement.classList.toggle('is-owned', this.ownedCards.has(card.id));

      openButton.type = 'button';
      openButton.className = 'mr-album__card-open';
      openButton.dataset.cardOpen = '';
      openButton.setAttribute(
        'aria-label',
        `Apri la carta ${card.number} di ${this.currentCollection.cards.length}: ${card.name}`
      );

      imageWrap.className = 'mr-album__card-image';
      image.src = card.image;
      image.alt = '';
      image.width = 571;
      image.height = 805;
      image.loading = 'lazy';
      image.decoding = 'async';

      copy.className = 'mr-album__card-copy';
      number.className = 'mr-album__card-number';
      number.textContent = String(card.number).padStart(2, '0');
      name.className = 'mr-album__card-name';
      name.textContent = card.name;

      ownedButton.type = 'button';
      ownedButton.className = 'mr-album__owned-toggle';
      ownedButton.dataset.ownedToggle = '';
      ownedButton.dataset.ownedVariant = 'card';
      ownedButton.innerHTML =
        '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="m4 10 4 4 8-9"/></svg><span></span>';
      this.updateOwnedButton(ownedButton, card);

      imageWrap.append(image);
      copy.append(number, name);
      openButton.append(imageWrap, copy);
      cardElement.append(openButton, ownedButton);
      fragment.append(cardElement);
    });

    this.grid.replaceChildren(fragment);
    this.grid.hidden = cards.length === 0;
    this.emptyState.hidden = cards.length !== 0;
    this.updateEmptyState();
  }

  updateEmptyState() {
    if (!this.emptyTitle || !this.emptyCopy) return;

    if (this.query) {
      this.emptyTitle.textContent = 'Nessuna carta trovata';
      this.emptyCopy.textContent = 'Prova a cercare un altro nome.';
      return;
    }

    if (this.ownershipFilter === 'owned') {
      this.emptyTitle.textContent = 'La tua collezione è ancora vuota';
      this.emptyCopy.textContent = 'Usa il pulsante “Ce l’ho” per aggiungere le tue carte.';
      return;
    }

    if (this.ownershipFilter === 'missing') {
      this.emptyTitle.textContent = 'Collezione completata';
      this.emptyCopy.textContent = 'Hai aggiunto tutte le carte di questa collezione.';
      return;
    }

    this.emptyTitle.textContent = 'Nessuna carta trovata';
    this.emptyCopy.textContent = 'Prova a cambiare i filtri.';
  }

  toggleOwned(cardId) {
    if (!this.validCardIds.has(cardId)) return;

    const collection = this.data.collections.find((item) =>
      item.cards.some((card) => card.id === cardId)
    );
    const wasComplete = this.isCollectionComplete(collection);
    const wasOwned = this.ownedCards.has(cardId);

    if (wasOwned) {
      this.ownedCards.delete(cardId);
    } else {
      this.ownedCards.add(cardId);
    }

    this.saveOwnedCards();
    this.updateProgress();
    this.renderCards();

    const currentCard = this.currentCollection?.cards[this.currentCardIndex];
    if (currentCard) this.updateOwnedButton(this.modalOwnedButton, currentCard);

    this.queueAccountUpdate(cardId, this.ownedCards.has(cardId));

    if (!wasOwned && !wasComplete && this.isCollectionComplete(collection)) {
      this.celebrateCollection(collection);
    }
  }

  isCollectionComplete(collection) {
    return Boolean(
      collection?.cards?.length &&
      collection.cards.every((card) => this.ownedCards.has(card.id))
    );
  }

  celebrateCollection(collection) {
    if (!collection || !this.completionDialog) return;

    window.clearTimeout(this.completionShowTimer);
    window.clearTimeout(this.completionRippleTimer);

    if (this.modal?.open) this.closeModal();

    const cards = Array.from(this.grid.querySelectorAll('[data-card-id]'));
    cards.forEach((card, index) => {
      card.style.setProperty('--mr-completion-delay', `${Math.min(index * 32, 720)}ms`);
      card.classList.add('is-completion-ripple');
    });

    this.completionTitle.textContent = collection.title;
    this.completionCount.textContent = String(collection.cards.length);
    this.completionTotal.textContent = String(collection.cards.length);
    this.buildCompletionConfetti();

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.completionShowTimer = window.setTimeout(() => {
      if (typeof this.completionDialog.showModal === 'function') {
        if (!this.completionDialog.open) this.completionDialog.showModal();
      } else {
        this.completionDialog.setAttribute('open', '');
      }

      window.requestAnimationFrame(() => {
        this.completionDialog.classList.add('is-visible');
      });
    }, reduceMotion ? 0 : 620);

    this.completionRippleTimer = window.setTimeout(() => {
      cards.forEach((card) => {
        card.classList.remove('is-completion-ripple');
        card.style.removeProperty('--mr-completion-delay');
      });
    }, reduceMotion ? 20 : 1650);
  }

  buildCompletionConfetti() {
    if (!this.completionConfetti || this.completionConfetti.childElementCount) return;

    const colors = ['#7657ff', '#a991ff', '#f5822a', '#ffd2ad', '#ffffff'];
    const fragment = document.createDocumentFragment();

    for (let index = 0; index < 30; index += 1) {
      const piece = document.createElement('span');
      const start = (index * 37 + 7) % 100;
      const drift = ((index * 29) % 120) - 60;
      const rotation = 280 + ((index * 47) % 440);

      piece.style.setProperty('--mr-confetti-x', `${start}%`);
      piece.style.setProperty('--mr-confetti-drift', `${drift}px`);
      piece.style.setProperty('--mr-confetti-delay', `${(index % 10) * 55}ms`);
      piece.style.setProperty('--mr-confetti-duration', `${1250 + (index % 7) * 85}ms`);
      piece.style.setProperty('--mr-confetti-rotation', `${rotation}deg`);
      piece.style.setProperty('--mr-confetti-color', colors[index % colors.length]);
      fragment.append(piece);
    }

    this.completionConfetti.append(fragment);
  }

  closeCompletion() {
    window.clearTimeout(this.completionShowTimer);
    if (!this.completionDialog?.open) return;
    this.completionDialog.classList.remove('is-visible');

    window.setTimeout(() => {
      if (this.completionDialog?.open) this.completionDialog.close();
    }, 180);
  }

  updateOwnedButton(button, card) {
    if (!button || !card) return;

    const isOwned = this.ownedCards.has(card.id);
    const label = button.querySelector('span');
    const isCardButton = button.dataset.ownedVariant === 'card';

    button.classList.toggle('is-owned', isOwned);
    button.setAttribute('aria-pressed', String(isOwned));
    button.setAttribute(
      'aria-label',
      isOwned
        ? `Rimuovi ${card.name} dalla mia collezione`
        : `Aggiungi ${card.name} alla mia collezione`
    );

    if (label) {
      label.textContent = isCardButton
        ? (isOwned ? 'Aggiunta' : 'Ce l’ho')
        : (isOwned ? 'Nella mia collezione' : 'Aggiungi alla mia collezione');
    }
  }

  updateOwnershipFilterButtons() {
    this.root.querySelectorAll('[data-ownership-filter]').forEach((button) => {
      button.setAttribute(
        'aria-pressed',
        String(button.dataset.ownershipFilter === this.ownershipFilter)
      );
    });
  }

  updateProgress() {
    const totalCards = this.allCards.length;
    const totalOwned = this.ownedCards.size;
    const totalCollections = this.root.querySelectorAll(
      '.mr-album__collection-grid > .mr-album__collection'
    ).length;
    const totalPercent = totalCards ? Math.round((totalOwned / totalCards) * 100) : 0;

    this.setText('[data-total-owned-count]', totalOwned);
    this.setText('[data-total-card-count]', totalCards);
    this.setText('[data-total-progress-label]', `${totalPercent}%`);
    this.setText('[data-hero-collection-count]', totalCollections);
    this.setText('[data-hero-card-count]', totalCards);
    this.setText('[data-hero-owned-count]', totalOwned);

    const progress = this.root.querySelector('[data-total-progress]');
    const progressFill = this.root.querySelector('[data-total-progress-fill]');
    if (progress) progress.setAttribute('aria-valuenow', String(totalPercent));
    if (progressFill) progressFill.style.width = `${totalPercent}%`;

    this.root.querySelectorAll('[data-collection-owned-count]').forEach((node) => {
      const collection = this.collectionMap.get(node.dataset.collectionOwnedCount);
      if (!collection) return;
      node.textContent = collection.cards.filter((card) => this.ownedCards.has(card.id)).length;
    });

    this.data.collections.forEach((collection) => {
      const isComplete = this.isCollectionComplete(collection);
      const option = this.root.querySelector(
        `[data-collection-option="${collection.id}"]`
      );
      const patch = this.root.querySelector(
        `[data-completion-patch="${collection.id}"]`
      );

      option?.classList.toggle('is-complete', isComplete);
      if (patch) patch.hidden = !isComplete;
    });

    if (this.currentCollection) {
      const currentOwned = this.currentCollection.cards.filter((card) =>
        this.ownedCards.has(card.id)
      ).length;
      this.setText('[data-current-owned-count]', currentOwned);

      const currentLabel = this.root.querySelector('[data-current-completion-label]');
      const currentComplete = this.isCollectionComplete(this.currentCollection);
      if (currentLabel) currentLabel.hidden = !currentComplete;
      this.root
        .querySelector('.mr-album__browser-count')
        ?.classList.toggle('is-complete', currentComplete);
    }

    this.updateStorageNote();
  }

  updateStorageNote() {
    const note = this.root.querySelector('[data-storage-note]');
    if (!note) return;

    if (!this.storageAvailable) {
      note.textContent = 'Salvataggio temporaneo per questa sessione';
    } else if (this.syncState === 'loading') {
      note.textContent = 'Connessione al tuo account…';
    } else if (this.syncState === 'saving') {
      note.textContent = 'Salvataggio nel tuo account…';
    } else if (this.syncState === 'synced') {
      note.textContent = 'Sincronizzata con il tuo account';
    } else if (this.syncState === 'error') {
      note.textContent = 'Salvata sul dispositivo · sincronizzazione da riprovare';
    } else {
      note.textContent = 'Salvata su questo dispositivo';
    }

    if (this.syncLoginLink) {
      this.syncLoginLink.hidden = !(
        this.syncConfig.enabled && this.syncState === 'signed_out'
      );
      this.syncLoginLink.href = this.syncConfig.loginUrl;
    }
  }

  setSyncState(state) {
    this.syncState = state;
    this.updateStorageNote();
  }

  setText(selector, value) {
    const node = this.root.querySelector(selector);
    if (node) node.textContent = String(value);
  }

  openCard(index, options = {}) {
    const cards = this.currentCollection?.cards;
    if (!cards?.length) return;

    this.currentCardIndex = ((index % cards.length) + cards.length) % cards.length;
    const card = cards[this.currentCardIndex];

    this.modalImage.src = card.image;
    this.modalImage.alt = `${this.currentCollection.title}, carta ${card.number}: ${card.name}`;
    this.modalCollection.textContent = this.currentCollection.title;
    this.modalNumber.textContent =
      `Carta ${String(card.number).padStart(2, '0')} / ${cards.length}`;
    this.modalTitle.textContent = card.name;
    this.modalDescription.textContent = this.currentCollection.description;
    this.updateOwnedButton(this.modalOwnedButton, card);
    this.resetCopyButton();

    if (options.updateUrl !== false) {
      this.updateUrl({ collection: this.currentCollection.id, card: card.id });
    }

    if (typeof this.modal.showModal === 'function') {
      if (!this.modal.open) this.modal.showModal();
    } else {
      this.modal.setAttribute('open', '');
    }
  }

  closeModal() {
    if (!this.modal) return;
    if (typeof this.modal.close === 'function' && this.modal.open) {
      this.modal.close();
    } else {
      this.modal.removeAttribute('open');
      this.updateUrl({ card: null });
    }
  }

  moveCard(direction) {
    this.openCard(this.currentCardIndex + direction);
  }

  async copyCardLink() {
    const cards = this.currentCollection?.cards;
    if (!cards?.length) return;

    const card = cards[this.currentCardIndex];
    const url = new URL(window.location.href);
    url.searchParams.set('collection', this.currentCollection.id);
    url.searchParams.set('card', card.id);

    try {
      await navigator.clipboard.writeText(url.toString());
      const label = this.copyLinkButton.querySelector('span');
      if (label) label.textContent = 'Link copiato';
    } catch (error) {
      window.prompt('Copia il link della carta:', url.toString());
    }
  }

  resetCopyButton() {
    const label = this.copyLinkButton?.querySelector('span');
    if (label) label.textContent = 'Copia il link della carta';
  }

  updateUrl({ collection, card } = {}) {
    const url = new URL(window.location.href);

    if (collection) url.searchParams.set('collection', collection);
    if (collection === null) url.searchParams.delete('collection');
    if (card) url.searchParams.set('card', card);
    if (card === null) url.searchParams.delete('card');

    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }

  normalize(value = '') {
    return value
      .toLocaleLowerCase('it')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }
}

const initializeMRCardAlbums = (scope = document) => {
  scope.querySelectorAll('[data-mr-album]').forEach((album) => {
    if (album.dataset.initialized === 'true') return;
    album.dataset.initialized = 'true';
    new MRCardAlbum(album);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeMRCardAlbums());
} else {
  initializeMRCardAlbums();
}

document.addEventListener('shopify:section:load', (event) => {
  initializeMRCardAlbums(event.target);
});
